import React from 'react';
import { BsDownload } from 'react-icons/bs';
import { exportPropertyConciergeMatrices } from '../utils/interventionHoursExport';

/**
 * Month-based property × concierge hour matrix exports.
 * Drop into Clients / Concierges extraction panels.
 */
export default function HoursMatrixExport({ clients, concierges, setError }) {
    const now = new Date();
    const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    const [monthValue, setMonthValue] = React.useState(defaultMonth);
    const [loadingMode, setLoadingMode] = React.useState(null);

    const runExport = async (mode) => {
        if (!monthValue) {
            setError({
                level: 'warning',
                message: 'Veuillez sélectionner un mois',
                btn1Text: 'OK',
                btn2hidden: true
            });
            return;
        }

        try {
            setLoadingMode(mode);
            setError({
                level: 'info',
                message: `Calcul des heures pour ${monthValue}...`,
                btn1Text: 'Patienter...',
                btn1hidden: true,
                btn2hidden: true
            });

            const result = exportPropertyConciergeMatrices({
                clients: clients || [],
                concierges: concierges || [],
                monthValue,
                mode
            });

            if (result.empty) {
                setError({
                    level: 'warning',
                    message: 'Aucune intervention trouvée pour le mois sélectionné',
                    btn1Text: 'OK',
                    btn2hidden: true
                });
                return;
            }

            const label = mode === 'property' ? 'par propriété' : 'par concierge';
            const anomalies = result.anomalyCount
                ? ` ${result.anomalyCount} anomalie(s) en bas de feuille.`
                : '';
            setError({
                level: 'success',
                message:
                    `Export ${label} terminé : ${result.propertyCount} propriété(s), ` +
                    `${result.conciergeCount} concierge(s), total ${result.totalHours.toFixed(2)} h.` +
                    anomalies,
                btn1Text: 'OK',
                btn2hidden: true
            });
        } catch (error) {
            console.error('Hours matrix export error:', error);
            setError({
                level: 'error',
                message: `Erreur lors de l'export: ${error.message || 'Erreur inconnue'}`,
                btn1Text: 'OK',
                btn2hidden: true
            });
        } finally {
            setLoadingMode(null);
        }
    };

    return (
        <div className="mt-4 pt-3 border-top">
            <div className="row align-items-end">
                <div className="col-12 mb-3">
                    <h5>Matrice heures propriété × concierge (mois)</h5>
                    <p className="small text-muted mb-0">
                        Deux exports pour un mois choisi : lignes = propriétés (colonnes = concierges),
                        ou lignes = concierges (colonnes = propriétés). Les concierges archivés restent inclus.
                        Seul sur l'intervention, un concierge validé compte la durée entière ; à plusieurs,
                        chacun compte les heures qu'il a saisies, et les heures du concierge manquant sont
                        déduites du reste s'il est le seul à manquer. Les cas qui demandent une vérification
                        sont listés dans un bloc « Anomalies » en bas de la feuille.
                    </p>
                </div>
                <div className="col-md-4 mb-3">
                    <label className="form-label" htmlFor="hoursMatrixMonth">
                        Mois <span className="text-danger">*</span>
                    </label>
                    <input
                        id="hoursMatrixMonth"
                        type="month"
                        className="form-control"
                        value={monthValue}
                        onChange={(e) => setMonthValue(e.target.value)}
                        required
                    />
                </div>
                <div className="col-md-4 mb-3">
                    <button
                        type="button"
                        className="btn btn-outline-success w-100"
                        onClick={() => runExport('property')}
                        disabled={!!loadingMode || !monthValue}
                    >
                        {loadingMode === 'property' ? (
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        ) : (
                            <BsDownload className="me-2" />
                        )}
                        Export par propriété
                    </button>
                </div>
                <div className="col-md-4 mb-3">
                    <button
                        type="button"
                        className="btn btn-outline-success w-100"
                        onClick={() => runExport('concierge')}
                        disabled={!!loadingMode || !monthValue}
                    >
                        {loadingMode === 'concierge' ? (
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        ) : (
                            <BsDownload className="me-2" />
                        )}
                        Export par concierge
                    </button>
                </div>
            </div>
        </div>
    );
}
